// ============================================================
//  wikiAPI.js  —  Wikipedia / Wikidata integration layer
//
//  Anomaly generation pipeline (in priority order):
//    1. OpenAI (via openaiAPI.js) — theme-aware, natural language
//    2. Wikidata fallback — structured property extraction
//
//  Navigation: The anomaly's searchTerm determines the NEXT page.
//  Clicking the anomaly link navigates to that entity's Wikipedia
//  page, which becomes the next game page. This creates a natural
//  chain through Wikipedia, keeping themed hunts within their theme.
//
//  Link density by difficulty (# of clickable /wiki/ links shown):
//    Scholar   → max  8  (few distractors — easy to spot the anomaly)
//    Detective → max 18  (moderate noise)
//    Oracle    → all     (maximum noise — every link is a suspect)
//
///  Page length: SAME for all difficulties (~3 400 chars = one 1080p screen)
//    All difficulties fetch the full article; limitPageLength trims uniformly.
//    The ONLY per-difficulty difference is hyperlink density (see LINK_CAPS).
// ============================================================

import {
  generateAnomalyWithAI,
  validateAnomalyResult,
  generateLinkSwapAnomalyWithAI,
  validateLinkSwapResult,
} from "./openaiAPI";
import { SPORTS_DOMAINS, POPCULTURE_DOMAINS } from "./openaiAPI";

const WIKI_API     = "https://en.wikipedia.org/w/api.php";
const WIKIDATA_API = "https://www.wikidata.org/w/api.php";

// ─────────────────────────────────────────────────────────────
//  THEME POOLS
// ─────────────────────────────────────────────────────────────
export const THEMES = {
  presidents: {
    label: "Presidents", emoji: "🏛️",
    pages: [
      "George Washington","Abraham Lincoln","Franklin D. Roosevelt",
      "John F. Kennedy","Barack Obama","Ronald Reagan","Bill Clinton",
      "Theodore Roosevelt","Dwight D. Eisenhower","Harry S. Truman",
      "Thomas Jefferson","Woodrow Wilson","Andrew Jackson","James Madison",
      "George H. W. Bush","George W. Bush","Jimmy Carter","Richard Nixon",
      "James Monroe","John Quincy Adams","Martin Van Buren","James K. Polk",
      "Ulysses S. Grant","William McKinley","William Howard Taft","Warren G. Harding",
    ],
  },
  countries: {
    label: "Countries", emoji: "🌍",
    pages: [
      "United States","United Kingdom","France","Germany","Japan",
      "China","Brazil","India","Canada","Australia","Italy","Spain",
      "Mexico","South Korea","Russia","Argentina","Egypt","South Africa",
      "Turkey","Saudi Arabia","Indonesia","Netherlands","Sweden","Norway",
    ],
  },
  popculture: {
    label: "Pop Culture", emoji: "🎬",
    pages: [
      "Michael Jackson","Taylor Swift","Elvis Presley","The Beatles",
      "Beyoncé","Marilyn Monroe","Oprah Winfrey","Steve Jobs",
      "Elon Musk","Bill Gates","Albert Einstein","Walt Disney",
      "Leonardo da Vinci","William Shakespeare","Freddie Mercury","Audrey Hepburn",
      "David Bowie","Madonna","Prince (musician)","Bob Dylan",
    ],
  },
  sports: {
    label: "Sports", emoji: "⚽",
    pages: [
      "Michael Jordan","LeBron James","Serena Williams","Tiger Woods",
      "Muhammad Ali","Usain Bolt","Lionel Messi","Cristiano Ronaldo",
      "Roger Federer","Rafael Nadal","Wayne Gretzky","Babe Ruth",
      "Tom Brady","Simone Biles","Michael Phelps","Kobe Bryant",
      "Pelé","Steffi Graf","Carl Lewis","Jesse Owens",
    ],
  },
  trivia: {
    label: "Trivia", emoji: "🧠",
    pages: [
      "Eiffel Tower","Great Wall of China","Colosseum","Statue of Liberty",
      "Mount Everest","Amazon River","Sahara Desert","Great Barrier Reef",
      "Niagara Falls","Grand Canyon","Machu Picchu","Stonehenge",
      "Taj Mahal","Pyramids of Giza","Easter Island","Angkor Wat",
      "Chichen Itza","Acropolis of Athens","Petra","Great Zimbabwe",
    ],
  },
  random: {
    label: "Random", emoji: "🎲",
    // Scholar — instantly recognisable brands, people, and places
    scholar: [
      "Netflix","McDonald's","YouTube","Instagram","iPhone","Coca-Cola",
      "Amazon (company)","Google","Facebook","Twitter","Nike, Inc.",
      "Taylor Swift","Michael Jordan","Harry Potter","Star Wars","Marvel Comics",
      "Minecraft","Barack Obama","Eiffel Tower","Mount Everest",
    ],
    // Detective — well-known events/people most people have studied or heard of;
    // a curious non-expert can spot a plausible-but-wrong fact with general knowledge.
    // Removed: Roman Empire, Renaissance, Silk Road, Ancient Egypt (too specialist).
    detective: [
      "World War II","Moon landing","Cold War","American Civil War",
      "Black Death","Industrial Revolution","Cuban Missile Crisis",
      "Berlin Wall","Space Race","Nelson Mandela","World War I",
      "Titanic","Martin Luther King Jr.","Steve Jobs","Great Wall of China",
      "French Revolution","Olympic Games","NASA","Charles Darwin",
    ],
    // Oracle — requires real specialist knowledge: ancient history, obscure treaties,
    // scientific concepts, medieval politics. Roman Empire moved here.
    oracle: [
      "Treaty of Versailles","Planck constant","Higgs boson","Magna Carta",
      "Thirty Years' War","Habsburg monarchy","Peloponnesian War",
      "Congress of Vienna","Sykes–Picot Agreement","Heliocentric model",
      "Fermat's Last Theorem","Byzantine Empire","Carolingian dynasty",
      "Weimar Republic","Meiji Restoration","Reconquista",
      "Roman Empire","Renaissance","Silk Road","Ancient Egypt",
    ],
  },
  custom: { label: "You Choose", emoji: "✏️", pages: [] },
};

// ─────────────────────────────────────────────────────────────
//  TITLE FETCHING
// ─────────────────────────────────────────────────────────────

export async function fetchRandomTitle() {
  const p = new URLSearchParams({
    action:"query", list:"random", rnnamespace:"0", rnlimit:"1",
    format:"json", origin:"*",
  });
  const res  = await fetch(`${WIKI_API}?${p}`);
  const data = await res.json();
  return data.query.random[0].title;
}

/** Return a starting title from the appropriate theme pool. */
export async function fetchThemeTitle(themeId, difficulty, customTitle = null) {
  if (customTitle) return customTitle;
  if (themeId === "custom") return fetchRandomTitle();
  if (themeId === "random") {
    const pool = THEMES.random[difficulty] || THEMES.random.detective;
    return pool[Math.floor(Math.random() * pool.length)];
  }
  // Theme integrity: Oracle should still respect the chosen theme.
  // Only the Random theme is allowed to pull from fully-random Wikipedia.
  const pool = THEMES[themeId]?.pages;
  if (!pool || pool.length === 0) return fetchRandomTitle();
  return pool[Math.floor(Math.random() * pool.length)];
}

/** Wikipedia autocomplete — used by the custom-article search box. */
export async function searchWikipedia(query) {
  if (!query || query.trim().length < 2) return [];
  const p = new URLSearchParams({
    action:"opensearch", search: query.trim(),
    limit:"8", namespace:"0", format:"json", origin:"*",
  });
  const res  = await fetch(`${WIKI_API}?${p}`);
  const data = await res.json();
  return data[1] || [];
}

/** Return the full page list for a given theme/difficulty combo. */
export function getThemePool(themeId, difficulty) {
  if (themeId === "random") return THEMES.random[difficulty] || THEMES.random.detective;
  return THEMES[themeId]?.pages || [];
}

// ─────────────────────────────────────────────────────────────
//  PAGE FETCH
//
//  All difficulties fetch the full article.
//  Page length is controlled uniformly in processHTML (limitPageLength).
//  Difficulty difference is expressed only through hyperlink density.
// ─────────────────────────────────────────────────────────────

export async function fetchPageData(title, difficulty = "detective") {
  const sectionParam = {}; // always fetch full article — length is trimmed in processHTML

  const [parseRes, propRes] = await Promise.all([
    fetch(`${WIKI_API}?${new URLSearchParams({
      action:"parse", page:title, prop:"text|links",
      redirects:"1",           // follow redirects so we don't render "Redirect to:" stubs
      ...sectionParam, disableeditsection:"1", format:"json", origin:"*",
    })}`),
    fetch(`${WIKI_API}?${new URLSearchParams({
      action:"query", titles:title, redirects:"1", prop:"pageprops", format:"json", origin:"*",
    })}`),
  ]);

  const parseData = await parseRes.json();
  const propData  = await propRes.json();
  if (parseData.error) throw new Error(parseData.error.info);

  const pages      = propData.query.pages;
  const pageKey    = Object.keys(pages)[0];
  const wikidataId = pages[pageKey]?.pageprops?.wikibase_item ?? null;

  return {
    html:  parseData.parse.text["*"],
    links: parseData.parse.links.filter((l) => l.ns === 0).map((l) => l["*"]),
    wikidataId,
    // Use the RESOLVED canonical title — fixes redirect breadcrumb bug
    // e.g. "Leningrad" request resolves to "Saint Petersburg" on Wikipedia
    title: parseData.parse.title ?? title,
  };
}

// ─────────────────────────────────────────────────────────────
//  WIKIDATA  —  fallback anomaly source
// ─────────────────────────────────────────────────────────────

const PROPS_PERSON = {
  // Dates are intentionally excluded from fallback anomalies (no P569).
  birthPlace:"P19", occupation:"P106",
  spouse:"P26",     position:"P39",   nationality:"P27",
};
const PROPS_ORG = {
  // Dates are intentionally excluded from fallback anomalies (no P571).
  headquarters:"P159", founder:"P112",
  industry:"P452", ceo:"P169",
};
const PROPS_PLACE = {
  // Dates are intentionally excluded from fallback anomalies (no P571).
  capital:"P36", headOfGov:"P6", continent:"P30",
};
const PROPS_EVENT = {
  // Dates are intentionally excluded from fallback anomalies (no P580).
  location:"P276",
};

async function fetchEntityClaims(wikidataId) {
  const p = new URLSearchParams({
    action:"wbgetentities", ids:wikidataId,
    props:"claims|labels", languages:"en", format:"json", origin:"*",
  });
  const res  = await fetch(`${WIKIDATA_API}?${p}`);
  const data = await res.json();
  return data.entities?.[wikidataId] ?? null;
}

async function fetchLabel(qid) {
  if (!qid) return null;
  const p = new URLSearchParams({
    action:"wbgetentities", ids:qid,
    props:"labels", languages:"en", format:"json", origin:"*",
  });
  const res  = await fetch(`${WIKIDATA_API}?${p}`);
  const data = await res.json();
  return data.entities?.[qid]?.labels?.en?.value ?? null;
}

// Date parsing helper intentionally removed from use; date/number anomalies are disallowed.

// Date claims are intentionally not used for anomalies in this project.

async function getEntityLabel(claims, propId) {
  const c = claims[propId]?.[0];
  if (!c) return null;
  return fetchLabel(c.mainsnak?.datavalue?.value?.id);
}

async function extractFacts(entity) {
  const claims = entity.claims;
  const facts  = [];

  const bp = await getEntityLabel(claims, PROPS_PERSON.birthPlace);
  if (bp)  facts.push({ type:"birthPlace",   label:"birthplace",            value:bp  });
  // spouse intentionally excluded — Wikidata returns single first names which
  // have no reliable Wikipedia page and produce awkward anomaly sentences.
  const oc = await getEntityLabel(claims, PROPS_PERSON.occupation);
  if (oc)  facts.push({ type:"occupation",   label:"occupation",            value:oc  });
  const po = await getEntityLabel(claims, PROPS_PERSON.position);
  if (po)  facts.push({ type:"position",     label:"position or role",      value:po  });
  const nat= await getEntityLabel(claims, PROPS_PERSON.nationality);
  if (nat) facts.push({ type:"nationality",  label:"nationality",           value:nat });

  const hq = await getEntityLabel(claims, PROPS_ORG.headquarters);
  if (hq)  facts.push({ type:"headquarters",label:"headquarters location", value:hq  });
  const fr = await getEntityLabel(claims, PROPS_ORG.founder);
  if (fr)  facts.push({ type:"founder",      label:"founder",               value:fr  });
  const ind= await getEntityLabel(claims, PROPS_ORG.industry);
  if (ind) facts.push({ type:"industry",     label:"industry",              value:ind });
  const ceo= await getEntityLabel(claims, PROPS_ORG.ceo);
  if (ceo) facts.push({ type:"ceo",          label:"CEO",                   value:ceo });

  const cap= await getEntityLabel(claims, PROPS_PLACE.capital);
  if (cap) facts.push({ type:"capital",      label:"capital city",          value:cap });
  const hg = await getEntityLabel(claims, PROPS_PLACE.headOfGov);
  if (hg)  facts.push({ type:"headOfGov",    label:"head of government",    value:hg  });
  const con= await getEntityLabel(claims, PROPS_PLACE.continent);
  if (con) facts.push({ type:"continent",    label:"continent",             value:con });

  const el = await getEntityLabel(claims, PROPS_EVENT.location);
  if (el)  facts.push({ type:"eventLocation",label:"location",              value:el  });

  return facts;
}

// ─────────────────────────────────────────────────────────────
//  WIKIDATA FALLBACK ANOMALY GENERATION
// ─────────────────────────────────────────────────────────────

const WRONG_PLACES = {
  scholar:   ["Antarctica","the Moon","Zimbabwe","Greenland","Papua New Guinea","Iceland","Madagascar"],
  detective: ["Toronto","Sydney","Cairo","Buenos Aires","Mumbai","Oslo","Nairobi"],
  oracle:    ["Newark","Oakland","Buffalo","Louisville","Fresno","Tucson","Omaha"],
};
const WRONG_JOBS = {
  scholar:   ["circus performer","shepherd","lighthouse keeper","taxidermist","mime artist"],
  detective: ["professor","journalist","diplomat","architect","surgeon"],
  oracle:    ["senior advisor","deputy director","chief of staff","undersecretary"],
};
// WRONG_NAMES: full proper names with guaranteed unambiguous Wikipedia pages.
// Single first names (Dorothy, Gerald, etc.) were removed — they hit disambiguation
// pages and produce awkward anomaly sentences. These are used for Wikidata fallback
// fact types: founder, ceo, headOfGov.
const WRONG_NAMES = {
  scholar:   ["Walt Disney","Elvis Presley","Napoleon Bonaparte","Queen Victoria","Julius Caesar"],
  detective: ["Henry Ford","Winston Churchill","Benjamin Franklin","Marie Curie","Nikola Tesla"],
  oracle:    ["Otto von Bismarck","Dwight D. Eisenhower","Woodrow Wilson","Georges Clemenceau","Helmut Kohl"],
};

// Note: date/number anomalies are intentionally disallowed for this project.
// Keep the helpers that depend on dates out of the generation path.

function pickFrom(pool) { return pool[Math.floor(Math.random() * pool.length)]; }

function pickPlace(difficulty) {
  const w = pickFrom(WRONG_PLACES[difficulty] || WRONG_PLACES.detective);
  return { display:w, searchTerm:w, hint:"The anomaly is related to a location." };
}
function pickName(difficulty) {
  const w = pickFrom(WRONG_NAMES[difficulty] || WRONG_NAMES.detective);
  return { display:w, searchTerm:w, hint:"The anomaly is related to a person's name." };
}
function pickJob(difficulty) {
  const w = pickFrom(WRONG_JOBS[difficulty] || WRONG_JOBS.detective);
  return { display:w, searchTerm:w, hint:"The anomaly is related to a job or role." };
}

function generateWrongValue(fact, difficulty) {
  switch (fact.type) {
    case "birthPlace":   return pickPlace(difficulty);
    case "spouse":       return pickName(difficulty);
    case "occupation":
    case "position":     return pickJob(difficulty);
    case "nationality":  return pickPlace(difficulty);
    case "headquarters": return pickPlace(difficulty);
    case "founder":      return pickName(difficulty);
    case "industry": {
      const p = {
        scholar:  ["beekeeping","puppet theatre","cheese-making","pirate radio"],
        detective:["agriculture","textile manufacturing","shipping","mining"],
        oracle:   ["telecommunications","financial services","aerospace","logistics"],
      };
      const w = pickFrom(p[difficulty] || p.detective);
      return { display:w, searchTerm:w, hint:"The anomaly is related to an industry." };
    }
    case "ceo":          return pickName(difficulty);
    case "capital":      return pickPlace(difficulty);
    case "headOfGov":    return pickName(difficulty);
    case "continent": {
      const all   = ["Africa","Antarctica","Asia","Europe","North America","Oceania","South America"];
      const wrong = all.filter((c) => c !== String(fact.value));
      const w     = pickFrom(wrong);
      return { display:w, searchTerm:w, hint:"The anomaly is related to a continent." };
    }
    case "eventLocation": return pickPlace(difficulty);
    default: return null;
  }
}

// ─────────────────────────────────────────────────────────────
//  WIKIPEDIA PAGE EXISTENCE CHECK
//
//  Before any anomaly is accepted, we verify its searchTerm
//  resolves to a real, non-disambiguation Wikipedia article.
//  This prevents navigation dead-ends like "Dorothy" (disambiguation)
//  or completely missing pages.
// ─────────────────────────────────────────────────────────────

async function verifyWikiPage(searchTerm) {
  if (!searchTerm || searchTerm.trim().length < 2) return false;
  try {
    const p = new URLSearchParams({
      action: "query",
      titles: searchTerm.trim(),
      redirects: "1",          // follow redirects so "Amazon rainforest" → real page
      prop: "pageprops",
      format: "json",
      origin: "*",
    });
    const res  = await fetch(`${WIKI_API}?${p}`);
    const data = await res.json();
    const pages = data.query?.pages;
    if (!pages) return false;
    const page = Object.values(pages)[0];
    // pageid -1 = page does not exist
    if (!page || page.missing !== undefined || page.pageid === -1) return false;
    // Disambiguation pages are useless as navigation targets
    if (page.pageprops?.disambiguation !== undefined) return false;
    return true;
  } catch {
    return false;
  }
}

// ─────────────────────────────────────────────────────────────
//  HTML PROCESSING HELPERS
// ─────────────────────────────────────────────────────────────

/**
 * Strip non-anomaly /wiki/ links down to a per-difficulty target count.
 * Excess links are replaced with plain <span> so they remain readable
 * but no longer respond to clicks.
 *
 * Scholar   → keep max  8 clickable links  (anomaly stands out clearly)
 * Detective → keep max 18 clickable links  (moderate noise)
 * Oracle    → keep all links              (maximum noise, no stripping)
 */
function stripLinksForDifficulty(root, difficulty) {
  const LINK_CAPS = { scholar: 8, detective: 18, oracle: Infinity };
  const cap = LINK_CAPS[difficulty] ?? 18;
  if (cap === Infinity) return;

  const regularLinks = Array.from(root.querySelectorAll("a"))
    .filter((a) => {
      const href = a.getAttribute("href") || "";
      return href.startsWith("/wiki/") && !a.classList.contains("anomaly-link");
    });

  if (regularLinks.length <= cap) return;

  // Shuffle so stripping is random (different links survive each game)
  const shuffled = [...regularLinks].sort(() => Math.random() - 0.5);
  const toStrip  = shuffled.slice(cap);
  const doc      = root.ownerDocument;

  toStrip.forEach((a) => {
    if (!a.parentNode) return;
    const span       = doc.createElement("span");
    span.textContent = a.textContent;
    a.parentNode.replaceChild(span, a);
  });
}

/**
 * Trim page content so the article fills one viewport without scrolling.
 *
 * ALL difficulties use the same character budget — the only difference between
 * Scholar / Detective / Oracle is hyperlink density (stripLinksForDifficulty).
 *
 * Budget: ~1 800 chars — roughly 3–5 substantive paragraphs depending on density.
 * A CSS max-height + overflow:hidden on .article-wrap acts as a hard safety net
 * so even dense articles (like long Obama paragraphs) never cause a scroll.
 *
 * After trimming paragraphs, orphaned section headings are removed.
 * Wikipedia wraps headings in <div class="mw-heading"> — we target that wrapper
 * directly so the whole block (not just the inner h2) gets deleted.
 */
function limitPageLength(root /*, difficulty unused — same budget for all */) {
  // Pre-filter: 2 500 chars — keeps roughly 3–5 paragraphs so the DOM
  // trimmer in GameScreen (useLayoutEffect) has enough content to work with
  // but never fetches an enormous wall of text.  The trimmer does the
  // pixel-perfect final cut; this budget is just a coarse upstream guard.
  const CHAR_BUDGET = 2500;

  const allParas = Array.from(root.querySelectorAll("p"))
    .filter((p) => p.textContent.trim().length >= 40);

  if (allParas.length === 0) return;

  // Walk paragraphs; mark the index where we first exceed the budget
  let total = 0;
  let cutAt = allParas.length - 1; // default: keep everything
  for (let i = 0; i < allParas.length; i++) {
    total += allParas[i].textContent.trim().length;
    if (total >= CHAR_BUDGET) { cutAt = i; break; }
  }

  // Keep paragraphs 0..cutAt (inclusive), remove everything after.
  // The DOM trimmer in GameScreen will do the precise pixel-level cut.
  allParas.slice(cutAt + 1).forEach((p) => p.remove());

  // Remove orphaned section headings.
  // Wikipedia uses <div class="mw-heading mw-heading2"><h2>…</h2></div>;
  // targeting the wrapper removes the entire block in one shot.
  // Also handles bare h2/h3/h4 in case the wrapper isn't present.
  const headingBlocks = Array.from(
    root.querySelectorAll(".mw-heading, h2:not(.mw-heading *), h3:not(.mw-heading *), h4:not(.mw-heading *)")
  );

  headingBlocks.forEach((block) => {
    if (!block.isConnected) return; // already gone via a parent removal

    // Walk siblings after this block to see if any paragraph remains
    let sib = block.nextElementSibling;
    let hasPara = false;
    while (sib) {
      // Stop at the next heading block
      if (sib.matches(".mw-heading, h2, h3, h4")) break;
      // Check if sib is or contains a substantive paragraph
      const paras = sib.matches("p")
        ? [sib]
        : Array.from(sib.querySelectorAll("p"));
      if (paras.some((p) => p.textContent.trim().length >= 40)) {
        hasPara = true;
        break;
      }
      sib = sib.nextElementSibling;
    }

    if (!hasPara) block.remove();
  });
}

// ─────────────────────────────────────────────────────────────
//  MAIN HTML PROCESSING
//
//  Pipeline per page:
//    1. Strip Wikipedia UI chrome (navboxes, cite errors, tables, etc.)
//    2. Inject anomaly into a randomly chosen content paragraph
//    3. Cap page length per difficulty
//    4. Strip excess links per difficulty
// ─────────────────────────────────────────────────────────────

function processHTML(html, difficulty, anomalyHTML) {
  const parser = new DOMParser();
  const doc    = parser.parseFromString(`<div id="root">${html}</div>`, "text/html");
  const root   = doc.getElementById("root");

  // ── Step 1: Strip Wikipedia UI clutter + all visual media ──
  root.querySelectorAll([
    // References & citations
    ".reflist",".references",".mw-references-wrap",".refbegin",
    "sup.reference","sup",
    // Navigation & meta boxes
    ".navbox",".sistersitebox",".mbox-small",".ambox",
    ".mw-parser-output > .ambox",".noprint",".hatnote",
    // Edit links and UI chrome
    ".mw-editsection",
    // Cite errors
    ".mw-ext-cite-error",".cite-error",".mw-message-box-error",
    "[class*='cite-error']","[class*='mw-ext-cite']",
    // Images, figures, thumbnails, galleries — and ALL their caption wrappers.
    // Wikipedia uses several different containers; we strip every known variant
    // so no orphaned caption boxes survive after the image itself is removed.
    "figure","figcaption","img",
    ".thumb",".thumbinner",".thumbcaption",".thumbimage",
    ".gallery",".gallerytable",".gallerybox",".gallerytext",
    ".mw-gallery-traditional",".mw-gallery-packed",
    ".mw-gallery-packed-overlay",".mw-gallery-packed-hover",
    ".mw-gallery-slideshow",".mw-gallery-nolines",
    "[typeof='mw:File']","[typeof='mw:File/Thumb']",
    "[typeof='mw:File/Frame']","[typeof='mw:File/Frameless']",
    // File description pages / image caption divs
    ".mw-file-description",".mw-file-element",
    ".mw-caption",".mw-caption-text",
    // Multi-image template wrappers (e.g. "Diagrams of RMS Titanic" boxes)
    ".tmulti",".tsingle",".trow",".theader",
    // floated image containers that survive figure/img removal
    ".floatleft",".floatright","div.tright","div.tleft",
    // Timeline boxes (e.g. "Timeline of RMS Titanic")
    ".timeline-wrapper",".EasyTimeline-container",
    "div[class*='timeline']",
    // Tables (infoboxes, wikitables, etc.)
    "table",".wikitable",".infobox",".infobox_v3",
    // Lists — bullet points and description lists
    "ul","ol","dl",
    // Audio/video
    "audio","video",
    // Quote / pull-quote boxes (e.g. "—Letter from Khrushchev")
    "blockquote",".templatequote",".pull-quote",".quotebox",
    ".template-quote",".cquote",
    // Wikipedia sidebar boxes ("Library resources about…")
    ".side-box",".side-box-right",".side-box-left",
    ".portal-bar",".sister-wikipedia",
    // Authority control / bibliography boxes
    ".mw-authority-control",".authority-control",
    // Category links
    ".catlinks",
    // Spoken article player
    ".spoken-wikipedia",
    // Coordinates / geo tags
    ".geo-default",".geo-nondefault",".coordinates",
  ].join(",")).forEach((el) => el.remove());

  // Remove paragraphs that are just cite errors, template noise, or quote attributions
  root.querySelectorAll("p").forEach((p) => {
    const t = p.textContent.trim();
    if (
      /Cite error/i.test(t) ||
      /\{\{efn\}\}/i.test(t) ||
      /\{\{reflist/i.test(t) ||
      /^—/.test(t)          // em-dash attribution lines ("—Philip Slavin")
    ) {
      p.remove();
    }
  });

  // Remove orphaned section headings — h2/h3/h4 that have no <p> content
  // between them and the next heading (happens when a section's content was
  // entirely tables/lists/images which were already stripped above).
  root.querySelectorAll("h2, h3, h4").forEach((heading) => {
    let next = heading.nextElementSibling;
    let hasContent = false;
    while (next && !["H2","H3","H4"].includes(next.tagName)) {
      if (next.tagName === "P" && next.textContent.trim().length > 0) {
        hasContent = true;
        break;
      }
      next = next.nextElementSibling;
    }
    if (!hasContent) heading.remove();
  });

  // Post-strip: remove any <div> or <section> that is now empty or contains
  // only whitespace — these are leftover wrappers from stripped galleries,
  // timeline boxes, or multi-image templates (e.g. "Diagrams of RMS Titanic").
  // Walk bottom-up so nested empties are caught in one pass.
  root.querySelectorAll("div, section").forEach((el) => {
    if (el.textContent.trim().length === 0) el.remove();
  });

  // ── Step 2: Cap page length ──────────────────────────────
  // MUST run before anomaly injection so the anomaly paragraph
  // is never cut off by the character budget.
  limitPageLength(root, difficulty);

  // ── Step 3: Inject anomaly sentence ─────────────────────
  // Injected AFTER length limiting so it always survives.
  // IMPORTANT:
  // - The anomaly must feel like part of the article, not a standalone inserted line.
  //   So we inject it INTO an existing paragraph (not as its own <p>).
  // - We still vary placement by choosing a difficulty-biased paragraph.
  if (anomalyHTML) {
    const survivingParas = Array.from(root.querySelectorAll("p"))
      .filter((p) => p.textContent.trim().length > 60);

    if (survivingParas.length > 0) {
      const n = survivingParas.length;
      let targetIdx = 0;

      if (difficulty === "scholar") {
        // early-biased
        const end = Math.max(0, Math.floor(n * 0.55));
        targetIdx = Math.floor(Math.random() * (end + 1));
      } else if (difficulty === "oracle") {
        // late-biased
        const start = Math.max(0, Math.floor(n * 0.45));
        targetIdx = start + Math.floor(Math.random() * Math.max(1, n - start));
      } else {
        // detective: anywhere
        targetIdx = Math.floor(Math.random() * n);
      }

      const tempDiv = doc.createElement("div");
      // Prepend a non-breaking space so the injected sentence doesn't run into
      // the previous character (Wikipedia HTML often ends without trailing space).
      tempDiv.innerHTML = "\u00a0" + anomalyHTML;

      const fragment = doc.createDocumentFragment();
      while (tempDiv.firstChild) fragment.appendChild(tempDiv.firstChild);

      const targetPara = survivingParas[Math.min(targetIdx, n - 1)];

      // Insertion position within the paragraph:
      // - Not always last sentence
      // - Avoid breaking words/sentences by inserting around existing <a> nodes when possible
      const modeRoll = Math.random();
      const preferMid = difficulty === "detective" ? 0.75 : difficulty === "oracle" ? 0.55 : 0.35;

      const anchors = Array.from(targetPara.querySelectorAll("a"))
        .filter((a) => !a.classList.contains("anomaly-link"));

      const insertAsChild = () => {
        // Fallback: safe append
        targetPara.appendChild(fragment);
      };

      const insertAtBeginning = () => {
        targetPara.insertBefore(fragment, targetPara.firstChild);
      };

      const insertNearAnchor = (after = true) => {
        if (anchors.length === 0) return false;
        const pick = anchors[Math.floor(Math.random() * anchors.length)];
        const ref = after ? pick.nextSibling : pick;
        targetPara.insertBefore(fragment, ref);
        return true;
      };

      if (modeRoll < 0.22) {
        // Beginning (sometimes)
        insertAtBeginning();
      } else if (modeRoll < 0.22 + preferMid) {
        // Middle-ish: near a random existing link to keep flow natural
        const ok = Math.random() < 0.6 ? insertNearAnchor(true) : insertNearAnchor(false);
        if (!ok) insertAsChild();
      } else {
        // End (sometimes)
        insertAsChild();
      }
    }
  }

  // ── Step 4: Strip excess links ───────────────────────────
  stripLinksForDifficulty(root, difficulty);

  return root.innerHTML;
}

function pickSwapAnchor(root, difficulty) {
  const paras = Array.from(root.querySelectorAll("p"))
    .filter((p) => p.textContent.trim().length > 80);
  if (paras.length === 0) return null;

  const n = paras.length;
  let pIdx = 0;
  if (difficulty === "scholar") {
    const end = Math.max(0, Math.floor(n * 0.55));
    pIdx = Math.floor(Math.random() * (end + 1));
  } else if (difficulty === "oracle") {
    const start = Math.max(0, Math.floor(n * 0.45));
    pIdx = start + Math.floor(Math.random() * Math.max(1, n - start));
  } else {
    pIdx = Math.floor(Math.random() * n);
  }

  const para = paras[Math.min(pIdx, n - 1)];
  const anchors = Array.from(para.querySelectorAll("a"))
    .filter((a) => {
      const href = a.getAttribute("href") || "";
      if (!href.startsWith("/wiki/")) return false;
      if (a.classList.contains("anomaly-link")) return false;
      // avoid special pages/fragments
      if (href.includes(":") || href.includes("#")) return false;
      return true;
    });
  if (anchors.length === 0) return null;

  return { para, anchor: anchors[Math.floor(Math.random() * anchors.length)] };
}

function getWikiTitleFromHref(href) {
  const m = String(href || "").match(/^\/wiki\/([^:#]+)$/);
  if (!m) return null;
  try {
    return decodeURIComponent(m[1]).replace(/_/g, " ");
  } catch {
    return null;
  }
}

async function buildLinkSwapAnomaly(pageData, difficulty, themeId) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div id="root">${pageData.html}</div>`, "text/html");
  const root = doc.getElementById("root");

  // Use the exact same cleanup/length/link-cap pipeline as normal pages:
  // remove clutter
  root.querySelectorAll([
    ".reflist",".references",".mw-references-wrap",".refbegin",
    "sup.reference","sup",
    ".navbox",".sistersitebox",".mbox-small",".ambox",
    ".mw-parser-output > .ambox",".noprint",".hatnote",
    ".mw-editsection",
    ".mw-ext-cite-error",".cite-error",".mw-message-box-error",
    "[class*='cite-error']","[class*='mw-ext-cite']",
    "figure","figcaption","img",
    ".thumb",".thumbinner",".thumbcaption",".thumbimage",
    ".gallery",".gallerytable",".gallerybox",".gallerytext",
    ".mw-gallery-traditional",".mw-gallery-packed",
    ".mw-gallery-packed-overlay",".mw-gallery-packed-hover",
    ".mw-gallery-slideshow",".mw-gallery-nolines",
    "[typeof='mw:File']","[typeof='mw:File/Thumb']",
    "[typeof='mw:File/Frame']","[typeof='mw:File/Frameless']",
    ".mw-file-description",".mw-file-element",
    ".mw-caption",".mw-caption-text",
    ".tmulti",".tsingle",".trow",".theader",
    ".floatleft",".floatright","div.tright","div.tleft",
    ".timeline-wrapper",".EasyTimeline-container",
    "div[class*='timeline']",
    "table",".wikitable",".infobox",".infobox_v3",
    "ul","ol","dl",
    "audio","video",
    "blockquote",".templatequote",".pull-quote",".quotebox",
    ".template-quote",".cquote",
    ".side-box",".side-box-right",".side-box-left",
    ".portal-bar",".sister-wikipedia",
    ".mw-authority-control",".authority-control",
    ".catlinks",
    ".spoken-wikipedia",
    ".geo-default",".geo-nondefault",".coordinates",
  ].join(",")).forEach((el) => el.remove());

  // coarse trim + link cap
  limitPageLength(root, difficulty);
  stripLinksForDifficulty(root, difficulty);

  const picked = pickSwapAnchor(root, difficulty);
  if (!picked) return null;

  const { para, anchor } = picked;
  const href = anchor.getAttribute("href") || "";
  const realValue = getWikiTitleFromHref(href) || anchor.textContent.trim();
  const paragraphText = para.textContent.trim();

  const usedArr = Array.from(usedWrongValues);
  const themePool = getThemePool(themeId, difficulty);

  // For Detective/Oracle, constrain wrongValue to entities already linked in the article.
  // This preserves tone/domain and prevents absurd inserts (e.g., novels "participating" in wars).
  const candidatePool = (difficulty === "detective" || difficulty === "oracle")
    ? (pageData.links || [])
        .map((t) => String(t || "").trim())
        .filter((t) =>
          t.length >= 2 &&
          !/\d/.test(t) &&
          !/disambiguation/i.test(t) &&
          t.toLowerCase() !== realValue.toLowerCase()
        )
        .slice(0, 60)
    : [];

  const ai = await generateLinkSwapAnomalyWithAI({
    articleTitle: pageData.title,
    paragraphText,
    realValue,
    difficulty,
    themeId,
    themePool,
    candidatePool,
    subjectDomain:
      themeId === "sports"
        ? (SPORTS_DOMAINS[pageData.title] || "")
        : themeId === "popculture"
          ? (POPCULTURE_DOMAINS[pageData.title] || "")
          : "",
    avoidValues: usedArr,
  });
  if (!ai) return null;

  const valid = validateLinkSwapResult(ai, {
    articleTitle: pageData.title,
    difficulty,
    realValue,
    paragraphText,
  });
  if (!valid) return null;

  const key = ai.wrongValue.toLowerCase();
  if (usedWrongValues.has(key)) return null;

  const ok = await verifyWikiPage(ai.searchTerm);
  if (!ok) return null;

  // Apply swap
  const newHref = `/wiki/${encodeURIComponent(ai.searchTerm.replace(/ /g, "_"))}`;
  anchor.setAttribute("href", newHref);
  anchor.classList.add("wiki-link", "anomaly-link");
  anchor.textContent = ai.wrongValue;

  usedWrongValues.add(key);
  visitedTitles.add(ai.searchTerm.toLowerCase());

  return {
    html: root.innerHTML,
    anomaly: {
      factType: "ai-linkswap",
      factLabel: ai.factLabel || "fact",
      realValue,
      wrongValue: ai.wrongValue,
      searchTerm: ai.searchTerm,
      hints: ai.hints || [],
      source: "ai",
    },
  };
}

// ─────────────────────────────────────────────────────────────
//  ANOMALY HTML BUILDERS
// ─────────────────────────────────────────────────────────────

function buildAnomalyHTMLFromWikidata(fact, wrongValue, title) {
  const href = `/wiki/${encodeURIComponent(wrongValue.searchTerm.replace(/ /g,"_"))}`;
  const link = (text) => `<a href="${href}" class="wiki-link anomaly-link">${text}</a>`;
  const wrap = (s)    => `<span class="anomaly-sentence">${s}</span>`;
  const subj = title ? title.replace(/\s*\([^)]+\)\s*$/, "").trim() : "This subject";

  switch (fact.type) {
    case "birthPlace":   return wrap(`${subj} was born in ${link(wrongValue.display)}.`);
    case "spouse":       return wrap(`${subj}'s spouse is ${link(wrongValue.display)}.`);
    case "occupation":   return wrap(`${subj} is known as a ${link(wrongValue.display)}.`);
    case "position":     return wrap(`${subj} has served as ${link(wrongValue.display)}.`);
    case "nationality":  return wrap(`${subj} holds ${link(wrongValue.display)} nationality.`);
    case "headquarters": return wrap(`${subj}'s headquarters are located in ${link(wrongValue.display)}.`);
    case "founder":      return wrap(`${subj} was founded by ${link(wrongValue.display)}.`);
    case "industry":     return wrap(`${subj} operates in the ${link(wrongValue.display)} industry.`);
    case "ceo":          return wrap(`${subj}'s current CEO is ${link(wrongValue.display)}.`);
    case "capital":      return wrap(`${subj}'s capital city is ${link(wrongValue.display)}.`);
    case "headOfGov":    return wrap(`${subj}'s head of government is ${link(wrongValue.display)}.`);
    case "continent":    return wrap(`${subj} is located in ${link(wrongValue.display)}.`);
    case "eventLocation":return wrap(`${subj} took place in ${link(wrongValue.display)}.`);
    default: return "";
  }
}

function buildAnomalyHTMLFromAI(aiResult) {
  const href    = `/wiki/${encodeURIComponent(aiResult.searchTerm.replace(/ /g,"_"))}`;
  const escaped = aiResult.wrongValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex   = new RegExp(escaped, "i");
  const linked  = aiResult.sentence.replace(regex, (match) =>
    `<a href="${href}" class="wiki-link anomaly-link">${match}</a>`
  );
  return `<span class="anomaly-sentence">${linked}</span>`;
}

// ─────────────────────────────────────────────────────────────
//  PLAIN TEXT EXTRACTION  (for AI context)
// ─────────────────────────────────────────────────────────────

function extractFirstParagraph(html) {
  const doc   = new DOMParser().parseFromString(html, "text/html");
  const paras = Array.from(doc.querySelectorAll("p"));
  // Grab first 3 substantive paragraphs for richer AI context
  return paras
    .filter((p) => p.textContent.trim().length > 100)
    .slice(0, 3)
    .map((p) => p.textContent.trim())
    .join("\n\n");
}

// ─────────────────────────────────────────────────────────────
//  PAGE QUALITY FILTER
// ─────────────────────────────────────────────────────────────

const BAD_TITLE_PATTERNS = [
  /^\d+$/,
  /^\d{4}$/,
  /^\d{4} in /i,
  /^list of/i,
  /^index of/i,
  /disambiguation/i,
  /^\d+(st|nd|rd|th) /i,
  /^deaths in/i, /^births in/i, /^events in/i,
  /^january|^february|^march|^april|^may|^june|^july|^august|^september|^october|^november|^december/i,
  /^monday|^tuesday|^wednesday|^thursday|^friday|^saturday|^sunday/i,
];

const MIN_LINKS    = { scholar: 3, detective: 4, oracle: 2 };
const MIN_TEXT_LEN = 300;

function isQualityPage(title, html, links, difficulty) {
  if (BAD_TITLE_PATTERNS.some((p) => p.test(title.trim()))) return false;
  if (links.length < (MIN_LINKS[difficulty] ?? 3)) return false;
  const doc     = new DOMParser().parseFromString(html, "text/html");
  const textLen = doc.body.innerText?.length ?? doc.body.textContent?.length ?? 0;
  return textLen >= MIN_TEXT_LEN;
}

// ─────────────────────────────────────────────────────────────
//  SESSION MEMORY
//
//  usedWrongValues:    prevents reusing the same navigation target
//  visitedTitles:      prevents navigation loops within a session
//  usedFactCategories: ensures fact-type variety across pages
//                      (e.g. no two pages both use "date" anomalies)
// ─────────────────────────────────────────────────────────────

const usedWrongValues    = new Set();
const visitedTitles      = new Set();
const usedFactCategories = new Set();

export function resetSession() {
  usedWrongValues.clear();
  visitedTitles.clear();
  usedFactCategories.clear();
}

// ─────────────────────────────────────────────────────────────
//  MAIN EXPORT — buildGamePage
//
//  Full pipeline:
//    1. Fetch Wikipedia article (section 0 for Scholar, full for others)
//    2. Quality gate (skipped when forceTitle=true)
//    3. Extract plain text for AI context
//    4. TRY: AI anomaly (theme-aware, difficulty-calibrated)
//    5. FALLBACK: Wikidata property extraction
//    6. Inject → limit length → strip links → return
//
//  Returned anomaly object always uses `hints: []` (never `hint: string`).
//
//  @param title       Article title (may be a redirect)
//  @param difficulty  "scholar" | "detective" | "oracle"
//  @param themeId     Theme key for AI pool selection
//  @param maxRetries  How many different articles to try before giving up
//  @param forceTitle  When true, never substitute a random page.
//                     Used for anomaly-navigation loads so the player always
//                     lands on the exact page they clicked (not a substitute).
// ─────────────────────────────────────────────────────────────

export async function buildGamePage(
  title,
  difficulty = "detective",
  themeId    = "random",
  maxRetries = 8,
  forceTitle = false,
) {
  // null/empty title with forceTitle=false → pick a fresh random theme title
  if (!title && !forceTitle) {
    title = await fetchThemeTitle(themeId);
  }
  if (title) visitedTitles.add(title.toLowerCase());

  const attempts = forceTitle ? Math.min(4, maxRetries) : maxRetries;

  for (let attempt = 0; attempt < attempts; attempt++) {
    const useTitle = (forceTitle || attempt === 0) ? title : await fetchRandomTitle();

    try {
      const pageData = await fetchPageData(useTitle, difficulty);

      if (!forceTitle && !isQualityPage(useTitle, pageData.html, pageData.links, difficulty)) continue;

      // ── 1. AI anomaly generation ──────────────────────────
      const themePool  = getThemePool(themeId, difficulty);
      const visitedArr = Array.from(visitedTitles);
      const firstPara  = extractFirstParagraph(pageData.html);

      // For SCHOLAR, prefer link-swap anomalies first (keeps Wikipedia tone and
      // allows us to force "comically obvious" wrong values).
      if (difficulty === "scholar") {
        const swapFirst = await buildLinkSwapAnomaly(pageData, difficulty, themeId);
        if (swapFirst?.anomaly?.searchTerm) {
          return {
            title: pageData.title,
            html: swapFirst.html,
            links: pageData.links,
            wikidataId: pageData.wikidataId,
            anomaly: swapFirst.anomaly,
          };
        }
      }

      const aiResult = await generateAnomalyWithAI({
        title:              pageData.title,                     // resolved canonical title
        firstParagraph:     firstPara,
        difficulty,
        themeId,
        themePool,
        visitedPages:       visitedArr,
        usedFactCategories: Array.from(usedFactCategories),     // properly tracked now
      });

      // Validate before accepting — catches cross-domain + grounding slips the prompt missed
      const aiValid = aiResult && validateAnomalyResult(aiResult, pageData.title, themeId, {
        contextText: firstPara,
        difficulty,
      });

      if (aiValid) {
        const key = aiResult.wrongValue.toLowerCase();
        if (!usedWrongValues.has(key)) {
          // Verify the searchTerm is a real, non-disambiguation Wikipedia page
          // before accepting — prevents dead-end navigation targets
          const pageExists = await verifyWikiPage(aiResult.searchTerm);
          if (!pageExists) {
            console.warn(`[WikiScav] AI searchTerm has no valid wiki page: "${aiResult.searchTerm}" — skipping`);
          } else {
            usedWrongValues.add(key);
            visitedTitles.add(aiResult.searchTerm.toLowerCase());
            if (aiResult.factCategory) usedFactCategories.add(aiResult.factCategory.toLowerCase());

            const anomalyHTML  = buildAnomalyHTMLFromAI(aiResult);
            const modifiedHTML = processHTML(pageData.html, difficulty, anomalyHTML);

            return {
              title:      pageData.title,
              html:       modifiedHTML,
              links:      pageData.links,
              wikidataId: pageData.wikidataId,
              anomaly: {
                factType:   "ai-generated",
                factLabel:  aiResult.factLabel,
                realValue:  aiResult.realValue,
                wrongValue: aiResult.wrongValue,
                searchTerm: aiResult.searchTerm,
                hints:      aiResult.hints || [],
                source:     "ai",
              },
            };
          }
        }
      }

      // ── 1b. AI link-swap anomaly (tone-preserving) ─────────
      // If the model's brand-new sentence is off-tone/low-quality, this path
      // keeps Wikipedia prose and swaps one existing link to a wrong entity.
      const swap = await buildLinkSwapAnomaly(pageData, difficulty, themeId);
      if (swap?.anomaly?.searchTerm) {
        return {
          title: pageData.title,
          html: swap.html,
          links: pageData.links,
          wikidataId: pageData.wikidataId,
          anomaly: swap.anomaly,
        };
      }

      // ── 2. Wikidata fallback ──────────────────────────────
      if (!pageData.wikidataId) continue;
      const entity = await fetchEntityClaims(pageData.wikidataId);
      if (!entity) continue;
      const facts = await extractFacts(entity);
      if (facts.length === 0) continue;

      const chosenFact = facts[Math.floor(Math.random() * facts.length)];
      const wrongValue = generateWrongValue(chosenFact, difficulty);
      if (!wrongValue) continue;
      if (usedWrongValues.has(wrongValue.display.toLowerCase())) continue;

      // Verify Wikidata wrong value also has a real Wikipedia page
      const wdPageExists = await verifyWikiPage(wrongValue.searchTerm);
      if (!wdPageExists) {
        console.warn(`[WikiScav] Wikidata searchTerm has no valid wiki page: "${wrongValue.searchTerm}" — skipping`);
        continue;
      }

      usedWrongValues.add(wrongValue.display.toLowerCase());

      const anomalyHTML  = buildAnomalyHTMLFromWikidata(chosenFact, wrongValue, pageData.title);
      const modifiedHTML = processHTML(pageData.html, difficulty, anomalyHTML);

      return {
        title:      pageData.title,
        html:       modifiedHTML,
        links:      pageData.links,
        wikidataId: pageData.wikidataId,
        anomaly: {
          factType:   chosenFact.type,
          factLabel:  chosenFact.label,
          realValue:  typeof chosenFact.value === "object" ? chosenFact.value.full : chosenFact.value,
          wrongValue: wrongValue.display,
          searchTerm: wrongValue.searchTerm,
          hints:      wrongValue.hint ? [wrongValue.hint] : [],
          source:     "wikidata",
        },
      };

    } catch {
      continue;
    }
  }

  // Last resort: return page without anomaly
  try {
    const pageData = await fetchPageData(title, difficulty);
    return {
      title:  pageData.title,
      html:   processHTML(pageData.html, difficulty, null),
      links:  pageData.links,
      wikidataId: pageData.wikidataId,
      anomaly: null,
    };
  } catch {
    throw new Error(`Failed to load "${title}" after ${attempts} attempts.`);
  }
}
